/**
 * What LunaPin looks like to an account that has never said otherwise.
 *
 * Three constants and one pure function, and the function is the interesting part. Every
 * value here is a *default*, which in this file means something precise: it sits strictly
 * beneath the user's own choice, it is never written to the datastore on their behalf, and
 * a later change to it therefore reaches everybody who has not chosen for themselves —
 * rather than pinning each account to whatever the default happened to be on the day they
 * first loaded the app.
 *
 * That is why [defaultedThemeSelectionJson] is applied on the way *out* of the persister
 * ([ServerPersister.read]) and not as a seed written on first boot. A seed would be
 * simpler and wrong twice: it would make "the default" a per-account historical fact, and
 * it would need a "have they got anything stored?" check that the read path already has
 * for free.
 *
 * The same rule reads back the other way. Nothing in this file may overwrite a stored
 * choice — see the two tiers in [defaultedThemeSelectionJson], which differ precisely
 * because "Auto" is a preference somebody can hold and the toolkit's built-in slot name
 * is not.
 *
 * The arrangement is Lunicle's, which solves the same problem in its own `ThemePersister`
 * (`applyBrandDefaultSelectionJson`). LunaPin has no brand manifest and no embed
 * parameters, so it needs the bottom tier of that ladder and nothing above it.
 *
 * ── Why this is in :client, having been in :web ──────────────────────────────
 *
 * It lived in `:web` for one stated reason: it names a toolkit type, and `:client` could
 * not depend on the toolkit. LPN-22 changed that — `:client` now has `lunula-core`, which
 * is the platform-free theme model rather than the web shell the old rule was about.
 *
 * Moving it is not tidying. **Without the move both phones would read the same blank
 * selection the browser does and paint the toolkit's own default**, so the same account
 * would be a different-looking app on three devices — from a rule that is written once and
 * was simply not reachable from two of them. `:web` goes on calling it through
 * `ServerPersister.read`, unchanged in behaviour.
 */
package se.soderbjorn.lunapin.client.theme

import se.soderbjorn.lunula.core.Appearance
import se.soderbjorn.lunula.core.DEFAULT_DARK_THEME
import se.soderbjorn.lunula.core.DEFAULT_LIGHT_THEME
import se.soderbjorn.lunula.core.ThemeSnapshotV2

/**
 * The theme in the dark slot for an account that has picked nothing: Citron Dark — "deep
 * mulberry ink under a highlighter citron", one of the toolkit's built-ins.
 *
 * LunaPin's own look, drawn for it rather than borrowed: the accent is highlighter ink,
 * which is what a bookmark manager is for. It replaced Crimson, which was the best fit
 * the catalog had before there was a LunaPin palette in it.
 *
 * Deliberately not the toolkit's own [DEFAULT_DARK_THEME] ("Lunamux Dark"): that name is
 * the sentinel meaning *never chosen*, which is exactly what
 * [defaultedThemeSelectionJson] keys off, and a default that collided with the sentinel
 * could never tell the two apart.
 */
const val LUNAPIN_DEFAULT_DARK_THEME: String = "Citron Dark"

/**
 * The light slot's companion: Citron Light — "mulberry ink on warm paper, marked in the
 * same citron".
 *
 * The other half of one theme rather than the nearest light neighbour: same mulberry, same
 * citron accent, warm paper in place of the ink. So the sun/moon toggle changes how bright
 * the app is without changing what app it is, and an account that switches appearance
 * twice a day never sees two different products.
 *
 * This is a stronger version of the pairing Crimson / Crimson Split gave before, and it is
 * why both halves are bound here: the toolkit's catalog holds them as a pair (see
 * `BuiltinThemes.kt`, where they sit contiguous for this reason) and naming only one of
 * them would leave the other slot on a palette nobody chose.
 */
const val LUNAPIN_DEFAULT_LIGHT_THEME: String = "Citron Light"

/**
 * Which slot a brand-new account starts in: the dark one, so a first load lands on
 * Citron Dark itself rather than on its paper-white counterpart.
 *
 * Not [Appearance.Auto] — the toolkit's own default — and the reason is the same one
 * Lunicle gives: with Auto, the first impression is a property of the visitor's machine,
 * and half of all first loads would show the palette that was *not* chosen as the app's
 * look. The dark one is the one the theme was drawn around.
 *
 * This is the one value here that only applies to a wholly empty account, because unlike a
 * slot name, [Appearance.Auto] is a position a user can deliberately hold. See the two
 * tiers in [defaultedThemeSelectionJson].
 */
val LUNAPIN_DEFAULT_APPEARANCE: Appearance = Appearance.Dark

/**
 * [stored] — the raw `darkness.theme.v2.selection` blob — with LunaPin's defaults applied
 * *beneath* whatever it contains.
 *
 * Two tiers, and they are not the same rule because the two fields do not have the same
 * notion of "unset":
 *
 *  - **Nothing stored at all** ⇒ the whole default, appearance included. This is the
 *    only case that can move the appearance, because [Appearance.Auto] is a choice — a
 *    user who wants their OS followed picks it, and swinging every Auto to Dark would
 *    silently overrule them on every load.
 *  - **Something stored, but a slot still holds the toolkit's built-in name** ⇒ that slot
 *    alone moves to LunaPin's. A slot holding [DEFAULT_DARK_THEME] is one the toolkit
 *    filled in for want of an answer, not one anybody chose, and this tier is what
 *    carries the default to an account that has only ever touched the sun/moon toggle or
 *    dragged a window — both of which persist a full selection blob with the sentinel
 *    names inside it. Without this tier, one unrelated click would pin an account to
 *    "Lunamux Dark" forever.
 *
 * The known cost of the second tier, stated rather than hidden: a user who explicitly
 * picks *"Lunamux Dark"* is indistinguishable from one who never chose, and gets Citron.
 * Lunicle carries the same caveat for the same reason — there is no per-slot "chosen" flag
 * in the persisted format to key off instead, and the alternative (treating an
 * appearance-only write as a theme choice) loses far more often.
 *
 * Everything not named here is passed through untouched, and the encode goes through
 * [ThemeSnapshotV2] rather than hand-written JSON so a change to the format cannot leave
 * this function quietly emitting last year's shape.
 *
 * @param stored the persisted selection blob, or null when the account has none.
 * @return the blob the toolkit should mount from. Never null: an account with nothing
 *   stored gets LunaPin's default rather than the toolkit's.
 */
fun defaultedThemeSelectionJson(stored: String?): String {
    if (stored.isNullOrBlank()) {
        return ThemeSnapshotV2(
            darkThemeName = LUNAPIN_DEFAULT_DARK_THEME,
            lightThemeName = LUNAPIN_DEFAULT_LIGHT_THEME,
            appearance = LUNAPIN_DEFAULT_APPEARANCE,
        ).selectionJson()
    }
    // Total by construction — a malformed or truncated blob parses to the defaults rather
    // than throwing, and then reads as "never chosen", which is the right answer for a
    // value nothing can be recovered from.
    val snapshot = ThemeSnapshotV2.fromStrings(selectionJson = stored, customThemesJson = null)
    return ThemeSnapshotV2(
        darkThemeName = snapshot.darkThemeName
            .takeUnless { it == DEFAULT_DARK_THEME } ?: LUNAPIN_DEFAULT_DARK_THEME,
        lightThemeName = snapshot.lightThemeName
            .takeUnless { it == DEFAULT_LIGHT_THEME } ?: LUNAPIN_DEFAULT_LIGHT_THEME,
        appearance = snapshot.appearance,
    ).selectionJson()
}

/**
 * The three constants above as one snapshot: what an account with nothing stored mounts on.
 *
 * Here so that "LunaPin's default look" is one value rather than three that could be assembled
 * differently in two places — [defaultedThemeSelectionJson] encodes it, and
 * `ThemeBackingViewModel.State` resolves it for the first frame of a phone that has not
 * connected to anything yet.
 *
 * That second caller is the reason this is a value and not a private detail of the function.
 * A phone with no server configured still has to paint *something*, and painting the toolkit's
 * own default there would mean an app that is Citron the moment it connects and Lunamux Dark
 * before — a visible flinch on first launch, saying nothing true about either state.
 */
val LUNAPIN_DEFAULT_SNAPSHOT: ThemeSnapshotV2 = ThemeSnapshotV2(
    darkThemeName = LUNAPIN_DEFAULT_DARK_THEME,
    lightThemeName = LUNAPIN_DEFAULT_LIGHT_THEME,
    appearance = LUNAPIN_DEFAULT_APPEARANCE,
)
